import {
  AfterContentInit,
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { environment as env } from '../../../../environments/environment';
import { Router, ActivatedRoute } from '@angular/router';
import { EMPTY, fromEvent, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators';
import { ApiService } from '../../../_shared/services/api.service';
import { UserService } from '../../../authenticated/services/user.service';
import { CardService } from '../../../authenticated/services/card.service';
import { Card } from 'src/app/_shared/models/card-models/card';
import { AdobeDtmService } from '../../../_shared/services/adobe-dtm.service';
import { PageInfo } from '../../../_shared/models/adobe/page-info';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { first } from 'rxjs/operators';
import { CardType } from '../../../_shared/models/card-models/CardType';
import {
  Offers,
  RedeemTile
} from 'src/app/_shared/models/redeem-points-models/redeem-points';
import { AccountResponse } from 'src/app/_shared/models/account-models/account-response';
import { EarningsResponse } from 'src/app/_shared/models/earning-models/earnings-response';
import { SummaryService } from 'src/app/authenticated/services/summary.service';
import { AemService } from 'src/app/_shared/services/aem-content.service';

@Component({
  selector: 'app-welcome',
  templateUrl: './welcome.component.html',
  styleUrls: ['./welcome.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class WelcomeComponent
  implements OnInit, OnDestroy, AfterContentInit, AfterViewInit
{
  componentDestroyed = new Subject<void>();
  applyNowFeatureFlag = false;
  digitalData: PageInfo;
  hasCard = false;
  step = 0;
  url: string;
  showModal: boolean = false;
  errorNotice: string;
  signOnPage: any;
  mobileHeader: any;
  header: any;
  card: any;
  notificationText: string;
  gmbCard: any;
  gmbCardMobile: any;

  currentOffer: Offers;
  offers: Offers[] = [];
  currentIndex = 0;
  pages: any[] = [];
  currentPage = 0;
  aemContent: any = '';
  showOfferModal: boolean = false;
  showRedeemModal: boolean = false;
  cardWidth = 396;
  totalPages = 0;
  currentTile: RedeemTile;
  tiles: RedeemTile[] = [];
  hasCardOffers: boolean;
  user: AccountResponse;
  cardMemberOffers: Offers[] = [];
  userSubscription: Subscription;
  currentRewardCard: Card;
  userEarnings: EarningsResponse;
  initialized: boolean = false;

  constructor(
    private readonly api: ApiService,
    private readonly cardService: CardService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly adobeDTMService: AdobeDtmService,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private readonly titleService: Title
  ) {}

  @ViewChild('carousel', { static: false })
  carousel!: ElementRef<HTMLDivElement>;
  @ViewChildren('cardEl') cardElements!: QueryList<ElementRef<HTMLDivElement>>;
  @HostListener('window:resize')
  onResize() {
    this.calculateTotalPages();
    this.setTileWidth();
  }

  ngOnInit(): void {
    this.getContent();
    this.setTileWidth();

    this.titleService.setTitle(
      'Earnings Account Management | GM Rewards Cards'
    );

    const error = this.route.snapshot.queryParamMap.get('error');
    if (error) {
      this.errorNotice =
        'Login unsuccessful. Login capability is only available on this website for Cardmembers with the GM Business Mastercard issued by Barclays Bank.';
    }

    this.cardService
      .getCurrentCard()
      .pipe(
        takeUntil(this.componentDestroyed),
        switchMap((card: Card) => {
          card ? (this.hasCard = true) : (this.hasCard = false);
          return this.api.getCustomer(card.trackId);
        })
      )
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(
        user => {
          this.userService.setUser(user);
          const cardType = this.mapCardPathToCardType(
            user?.accountDetails?.cardType
          );
          if (cardType === 12) {
            void this.router.navigate(['/dashboard']).then();
          } else {
            this.signOut('');
            void this.router.navigate(['*']).then();
          }
        },
        error => {
          //No card and no customer data
          this.hasCard = false;
        }
      );

    this.digitalData = {
      pageInfo: {
        cardType: 'common',
        cardTypeOwned: '',
        loginType: '',
        offerID: '',
        pageName: 'earnings.login | public | common',
        pageSubType: '',
        pageType: 'homepage'
      },
      formInfo: {
        formName: 'login',
        formFieldName: '',
        errorMessage: ''
      }
    };

    this.digitalData = this.adobeDTMService.setupWindowPageInfoData(
      this.digitalData,
      'welcome',
      'Welcome'
    );

    this.adobeDTMService.setWindowObjectData(this.digitalData);
    this.adobeDTMService.sendAdobeLaunchData();
  }

  private mapCardPathToCardType(cardPath: string): CardType {
    return cardPath.toString() === '12' ? 12 : 13;
  }

  ngAfterViewInit() {
    const waitForRender = setInterval(() => {
      if (this.initialized && this.carousel?.nativeElement) {
        this.calculateTotalPages();
        fromEvent(this.carousel.nativeElement, 'scroll')
          .pipe(debounceTime(100))
          .subscribe(() => this.handleScroll());
        clearInterval(waitForRender);
      }
    }, 0);
  }

  ngAfterContentInit(): void {
    this.sendTrackingInfo();
  }

  login(_window: Window = window): void {
    this.api
      .getSsoUrl('')
      .pipe(first())
      .subscribe(redirectUrl => {
        _window.location.href = redirectUrl.result;
      });
  }

  signOut(_window): void {
    _window = _window || window;
    this.api
      .getLogoutUrl()
      .pipe(first())
      .subscribe(redirectUrl => {
        this.cardService.clearCards();
        _window.location.href = redirectUrl.result;
      });
  }

  goToBarclaysSite(): void {
    window.open(
      'https://cards.barclaycardus.com/credit-card/ec2b687f-9a16-4f4a-a367-59d5f8892abb?referrerid=GMBGMCARDTIAN',
      '_blank'
    );
  }

  createAccount(_window: Window = window) {
    this.api
      .getCreateAccount()
      .pipe(first())
      .subscribe(redirectUrl => {
        _window.location.href = redirectUrl.result;
      });
  }

  sendTrackingInfo(): void {
    this.adobeDTMService.setWindowObjectData(this.digitalData);
  }

  ngOnDestroy(): void {
    this.componentDestroyed.next();
    this.componentDestroyed.unsubscribe();
  }

  notificationVisability() {
    const endDate = new Date(2025, 7, 22);
    const today = new Date();
    if (today <= endDate) {
      this.notificationText = this.signOnPage?.notification[0].notification;
    } else {
      this.notificationText = this.signOnPage?.notification[1].notification;
    }
  }

  getContent(): void {
    this.api.getAemPage('signOn').subscribe(data => {
      this.aemContent = data;
      this.signOnPage = JSON.parse(data.loginPage);
      this.tiles = JSON.parse(data.unauthOffers).Tiles;
      this.applyNowFeatureFlag = this.signOnPage.applyNowFeatureFlag;

      this.mobileHeader = `${environment.API_URL}/getAemImage?image=${this.signOnPage.headers.headerMobile}`;
      this.header = `${environment.API_URL}/getAemImage?image=${this.signOnPage.headers.header}`;
      this.card = `${environment.API_URL}/getAemImage?image=${this.signOnPage.cardImage}`;
      this.gmbCard = `${environment.API_URL}/getAemImage?image=${this.signOnPage.marketingPage.earnCard.cardImage}`;
      this.gmbCardMobile = `${environment.API_URL}/getAemImage?image=${this.signOnPage.marketingPage.earnCard.cardImageMobile}`;
      this.notificationVisability();

      this.tiles.forEach(list => {
        list.image = `${environment.API_URL}/getAemImage?image=${list.image}`;
        list.mobileImage = `${environment.API_URL}/getAemImage?image=${list.mobileImage}`;
      });
      this.initialized = true;
    });
  }

  setTileWidth(): void {
    this.cardWidth = window.innerWidth < 776 ? 316 : 396;
  }

  // Offer Carousel
  setCurrentOffer(): void {
    this.currentOffer = this.cardMemberOffers[this.currentIndex];
  }

  nextSlide() {
    this.currentIndex = (this.currentIndex + 1) % this.cardMemberOffers.length;
    this.setCurrentOffer();
  }

  prevSlide() {
    this.currentIndex =
      (this.currentIndex - 1 + this.cardMemberOffers.length) %
      this.cardMemberOffers.length;
    this.setCurrentOffer();
  }

  goToSlide(index: number) {
    this.currentIndex = index;
    this.setCurrentOffer();
  }

  // Redeem Buy Carousel
  get paginationArray(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i);
  }

  private getFullyVisibleCardIndices(): number[] {
    const container = this.carousel.nativeElement;
    const containerLeft = container.scrollLeft;
    const containerRight = containerLeft + container.offsetWidth;

    const indices: number[] = [];

    this.cardElements.forEach((cardEl, index) => {
      const card = cardEl.nativeElement;
      const cardLeft = card.offsetLeft;
      const cardRight = cardLeft + card.offsetWidth;

      if (cardLeft >= containerLeft && cardRight <= containerRight) {
        indices.push(index);
      }
    });

    return indices;
  }

  calculateTotalPages() {
    const container = this.carousel.nativeElement;
    const visibleCount = Math.floor(container.offsetWidth / this.cardWidth);
    this.totalPages = Math.ceil(this.tiles.length / visibleCount);
  }

  scrollLeft() {
    const firstFullyVisible = this.getFullyVisibleCardIndices()[0];
    const targetIndex = Math.max(firstFullyVisible - this.cardsPerPage(), 0);
    this.scrollToCard(targetIndex);
  }

  scrollRight() {
    const fullyVisible = this.getFullyVisibleCardIndices();
    const lastFullyVisible = fullyVisible[fullyVisible.length - 1] ?? 0;
    const targetIndex = Math.min(lastFullyVisible + 1, this.tiles.length - 1);
    this.scrollToCard(targetIndex);
  }

  goToPage(pageIndex: number) {
    const index = pageIndex * this.cardsPerPage();
    this.scrollToCard(index);
  }

  handleScroll() {
    const container = this.carousel.nativeElement;
    const scrollLeft = container.scrollLeft;
    const visibleWidth = container.offsetWidth;
    const totalScrollableWidth = container.scrollWidth;

    const cardsPerPage = this.cardsPerPage();
    const pageWidth = cardsPerPage * this.cardWidth;
    const maxPageIndex = this.totalPages - 1;

    const pageIndex = Math.round(scrollLeft / pageWidth);
    const nearEnd = scrollLeft + visibleWidth >= totalScrollableWidth - 1;

    if (nearEnd) {
      this.currentPage = maxPageIndex;
    } else {
      this.currentPage = pageIndex;
    }
  }

  private scrollToCard(index: number) {
    const targetCard = this.cardElements.get(index)?.nativeElement;
    if (targetCard) {
      const container = this.carousel.nativeElement;
      container.scrollTo({
        left: targetCard.offsetLeft,
        behavior: 'smooth'
      });
    }
  }

  private cardsPerPage(): number {
    const container = this.carousel.nativeElement;
    return Math.floor(container.offsetWidth / this.cardWidth);
  }

  openRedeemModal(selectedTile: RedeemTile): void {
    this.currentTile = selectedTile;
    this.showRedeemModal = true;
  }
}
